import {
  Boxes,
  ChevronRight,
  Film,
  Globe,
  Layers,
  Network,
  Radio,
  ShieldCheck,
  Smartphone,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { RevealSection } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

export function MulticastVsOttPage() {
  const { messages, site, href } = useSite();
  const u = (path: string) => localizeUrl(path, site.locale, href);
  const salesCta = u("/contacts?inquiry=sales#inquiry-form");

  const heroStats = [
    { icon: Radio, label: messages.moStat1 },
    { icon: Globe, label: messages.moStat2 },
    { icon: Layers, label: messages.moStat3 },
  ];

  const mcastItems = [
    messages.moMcast1,
    messages.moMcast2,
    messages.moMcast3,
    messages.moMcast4,
    messages.moMcast5,
    messages.moMcast6,
  ];
  const ottItems = [
    messages.moOtt1,
    messages.moOtt2,
    messages.moOtt3,
    messages.moOtt4,
    messages.moOtt5,
    messages.moOtt6,
  ];

  const tableRows = [
    { label: messages.moRow1Label, mcast: messages.moRow1Mcast, ott: messages.moRow1Ott },
    { label: messages.moRow2Label, mcast: messages.moRow2Mcast, ott: messages.moRow2Ott },
    { label: messages.moRow3Label, mcast: messages.moRow3Mcast, ott: messages.moRow3Ott },
    { label: messages.moRow4Label, mcast: messages.moRow4Mcast, ott: messages.moRow4Ott },
    { label: messages.moRow5Label, mcast: messages.moRow5Mcast, ott: messages.moRow5Ott },
    { label: messages.moRow6Label, mcast: messages.moRow6Mcast, ott: messages.moRow6Ott },
    { label: messages.moRow7Label, mcast: messages.moRow7Mcast, ott: messages.moRow7Ott },
    { label: messages.moRow8Label, mcast: messages.moRow8Mcast, ott: messages.moRow8Ott },
    { label: messages.moRow9Label, mcast: messages.moRow9Mcast, ott: messages.moRow9Ott },
    { label: messages.moRow10Label, mcast: messages.moRow10Mcast, ott: messages.moRow10Ott },
  ];

  const fitItems: { icon: LucideIcon; text: string }[] = [
    { icon: Radio, text: messages.moFit1 },
    { icon: Film, text: messages.moFit2 },
    { icon: Smartphone, text: messages.moFit3 },
    { icon: ShieldCheck, text: messages.moFit4 },
  ];

  const faqs = [
    { q: messages.moFaqQ1, a: messages.moFaqA1 },
    { q: messages.moFaqQ2, a: messages.moFaqA2 },
    { q: messages.moFaqQ3, a: messages.moFaqA3 },
    { q: messages.moFaqQ4, a: messages.moFaqA4 },
    { q: messages.moFaqQ5, a: messages.moFaqA5 },
    { q: messages.moFaqQ6, a: messages.moFaqA6 },
  ];

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={u("/")} className="hover:text-foreground">
            {messages.guidesBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={u("/guides")} className="hover:text-foreground">
            {messages.navGuides}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.moBreadcrumbPage}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div aria-hidden className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-40" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-0 -z-10 h-[440px] w-[600px] rounded-full opacity-40 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
          <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            {messages.moHeroEyebrow}
          </div>
          <h1
            className="mt-4 animate-fade-up text-balance text-4xl font-semibold tracking-tight sm:text-5xl"
            style={{ animationDelay: "80ms" }}
          >
            {messages.moHeroTitle}{" "}
            <span className="text-gradient">{messages.moHeroTitleAccent}</span>
          </h1>
          <p
            className="mx-auto mt-6 max-w-2xl animate-fade-up text-pretty text-lg text-muted-foreground"
            style={{ animationDelay: "160ms" }}
          >
            {messages.moHeroSub}
          </p>
          <p
            className="mt-5 animate-fade-up text-pretty text-sm font-medium text-foreground/80"
            style={{ animationDelay: "220ms" }}
          >
            {messages.moHeroNote}
          </p>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-3"
            style={{ animationDelay: "280ms" }}
          >
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.moHeroCtaPrimary}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <a href="#both">{messages.moHeroCtaSecondary}</a>
            </Button>
          </div>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-2"
            style={{ animationDelay: "340ms" }}
          >
            {heroStats.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
              >
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Thesis — it's about the network */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.moThesisEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.moThesisTitle}
          </h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>{messages.moThesisBody1}</p>
            <p>{messages.moThesisBody2}</p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-primary/30 bg-primary/[0.04] p-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Network className="h-4 w-4" />
                {messages.moThesisCoreLabel}
              </div>
              <p className="mt-2 text-lg font-semibold text-foreground">
                {messages.moThesisCoreValue}
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.04] p-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                <Globe className="h-4 w-4" />
                {messages.moThesisReachLabel}
              </div>
              <p className="mt-2 text-lg font-semibold text-foreground">
                {messages.moThesisReachValue}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* How each works — split panel */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.moDefineEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.moDefineTitle}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-primary/30 bg-card/70 p-6 sm:p-7">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Radio className="h-5 w-5" />
                </span>
                <h3 className="text-base font-semibold text-foreground">
                  {messages.moMcastColTitle}
                </h3>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{messages.moMcastColSub}</p>
              <ul className="mt-5 space-y-3">
                {mcastItems.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground/90">
                    <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-emerald-500/30 bg-card/70 p-6 sm:p-7">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <Globe className="h-5 w-5" />
                </span>
                <h3 className="text-base font-semibold text-foreground">{messages.moOttColTitle}</h3>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{messages.moOttColSub}</p>
              <ul className="mt-5 space-y-3">
                {ottItems.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground/90">
                    <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* Comparison table */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.moTableEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.moTableTitle}
            </h2>
          </div>
          <div className="mt-10 overflow-x-auto rounded-2xl border border-border/60 bg-card/60">
            <table className="w-full min-w-[680px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-muted-foreground">
                    {messages.moTableColDimension || " "}
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <Radio className="h-4 w-4 text-primary" />
                      {messages.moTableColMcast}
                    </span>
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <Globe className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      {messages.moTableColOtt}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => (
                  <tr key={r.label} className="border-b border-border/60 align-top last:border-0">
                    <th scope="row" className="px-4 py-3 text-left font-medium text-foreground/90">
                      {r.label}
                    </th>
                    <td className="px-4 py-3 text-muted-foreground">{r.mcast}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.ott}</td>
                  </tr>
                ))}
                <tr className="bg-primary/[0.06]">
                  <th scope="row" className="px-4 py-3 text-left font-semibold text-foreground">
                    {messages.moTableInextLabel}
                  </th>
                  <td colSpan={2} className="px-4 py-3 font-medium text-foreground/90">
                    <a
                      href={u("/solutions/iptv-app")}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {messages.moTableInextValue}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">{messages.moTableNote}</p>
        </div>
      </RevealSection>

      {/* The efficiency gap */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="rounded-2xl border border-primary/25 bg-card/70 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Zap className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-primary">
                  {messages.moScaleEyebrow}
                </p>
                <h2 className="mt-2 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                  {messages.moScaleTitle}
                </h2>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  {messages.moScaleBody}
                </p>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* When to use which */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.moFitListEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.moFitListTitle}
          </h2>
          <ul className="mt-6 space-y-3">
            {fitItems.map(({ icon: Icon, text }) => (
              <li
                key={text}
                className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 px-4 py-3"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-sm text-foreground/90">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </RevealSection>

      {/* Where we fit — one box, both transports */}
      <RevealSection id="both" className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.moBothEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.moBothTitle}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground">
            {messages.moBothBody}
          </p>
          <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-primary/20 bg-primary/[0.04] p-6 sm:flex-row sm:items-center">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Boxes className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-base font-semibold text-foreground">
                {messages.moBothNoteTitle}
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {messages.moBothNoteBody}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.moFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.moFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-16 text-background sm:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.moCtaEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.moCtaTitle}
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.moCtaBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.moCtaPrimary}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background sm:w-auto"
            >
              <a href={salesCta}>{messages.moCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}
